import { Country, useApiPostDealFinanceV1ProfitabilityCalculatorCreate } from 'api';
import type { UseFormReturn } from 'form';

import type { FormInputValues, FormValues } from '../../pages/DealProfitabilityCalculator/DealProfitabilityCalculator';

export const useDealProfitabilityCalculator = (form: UseFormReturn<FormInputValues, unknown, FormValues>) => {
  const { setError } = form;

  const { mutate, data, isPending } = useApiPostDealFinanceV1ProfitabilityCalculatorCreate({
    mutation: {
      onError(error) {
        setError('root', { message: error.message });
      },
    },
  });

  const handleCalculateProfitability = async (values: FormValues) => {
    const originCountryCode = values.origin_address?.country_code || values.origin_port_country || undefined;
    const destinationCountryCode =
      values.destination_address?.country_code || values.destination_port_country || undefined;

    const buyer_due_date_offset = values.buyer_due_date_offset * (values.buyer_due_date_offset_direction ?? 1);
    const seller_due_date_offset = values.seller_due_date_offset * (values.seller_due_date_offset_direction ?? 1);
    const originAddress = values.origin_address;
    const destinationAddress = values.destination_address;

    mutate({
      data: {
        buyer_agent_commission_currency: values.buyer_agent_commission_currency,
        buyer_agent_commission_per_tonne: values.buyer_agent_commission.toString(),
        buyer_currency: values.buyer_currency,
        buyer_deposit_percentage: values.buyer_deposit_percentage.toString(),
        buyer_due_date_offset,
        buyer_payment_event: values.buyer_payment_event,
        buyer_price_per_tonne: values.buyer_price_per_tonne.toString(),
        demand_incoterm: values.demand_incoterm,
        destination:
          destinationAddress && destinationAddress.country_code
            ? {
                country: (destinationAddress.country_code as Country) || '',
                lat: destinationAddress.latitude || 0,
                lon: destinationAddress.longitude || 0,
              }
            : undefined,
        destination_country: destinationCountryCode,
        estimated_transit_time_days: values.estimated_transit_time_days,
        material: values.material,
        origin:
          originAddress && originAddress.country_code
            ? {
                country: (originAddress.country_code as Country) || '',
                lat: originAddress.latitude || 0,
                lon: originAddress.longitude || 0,
              }
            : undefined,
        origin_country: originCountryCode,
        pern_currency: values.pern_currency,
        pern_revenue_per_tonne: values.pern_revenue_per_tonne.toString(),
        port_of_discharge_id: values.port_of_discharge || undefined,
        port_of_loading_id: values.port_of_loading || undefined,
        purchaser_payment_method: values.purchaser_payment_method,
        seller_agent_commission_currency: values.seller_agent_commission_currency,
        seller_agent_commission_per_tonne: values.seller_agent_commission.toString(),
        seller_currency: values.seller_currency,
        seller_deposit_percentage: values.seller_deposit_percentage.toString(),
        seller_due_date_offset,
        seller_payment_event: values.seller_payment_event,
        seller_price_per_tonne: values.seller_price_per_tonne.toString(),
        shipping_cost_per_load: values.shipping_cost_per_load.toString(),
        shipping_cost_per_load_currency: values.shipping_cost_per_load_currency,
        supply_incoterm: values.supply_incoterm,
        tonnes: values.tonnes.toString(),
        tonnes_per_load: values.tonnes_per_load.toString(),
        transport_cost_selection_info: {
          demand_incoterm: values.demand_incoterm,
          destination_location: values.destination_address?.full_address || '',
          destination_site_id: values.buyer_site || null,
          origin_location: values.origin_address?.full_address || '',
          origin_site_id: values.seller_site || null,
          port_of_discharge_id: values.port_of_discharge,
          port_of_loading_id: values.port_of_loading,
          source_choice: values.transport_cost_source,
          supply_incoterm: values.supply_incoterm,
          transport_cost: values.shipping_cost_per_load.toString(),
          transport_cost_currency: values.shipping_cost_per_load_currency,
        },
      },
    });
  };

  return { data, handleCalculateProfitability, isProcessing: isPending };
};
